import type {
  HTMLPerspectiveViewerElement,
  ViewerConfigUpdate
} from '@perspective-dev/viewer'
import type {
  HeaderRenderer,
  HTMLPerspectiveViewerDatagridPluginElement
} from '@perspective-dev/viewer-datagrid'
import type { RegularTableElement } from 'regular-table'

import {
  intersection,
  isDeepEqual,
  isEmpty,
  isNumber,
  last,
  map,
  omitBy,
  pick
} from 'remeda'
import { createIcon } from '../helpers/icon'
import { captureEffectError, pushEvent } from '../helpers/utils'
import type { ColumnsConfig as ExportColumnsConfig } from './export/descriptor'
import type { ExportDescriptorInput } from './export/export-client'
import { exportToBlob, exportToCsv } from './export/export-client'
import { NO_GROUP_LABEL } from './export/labels'
import {
  NG_PREFIX,
  NO_GROUP_SENTINEL,
  nullSafeGroupBy
} from './export/null-safe-group-by'
import {
  appendChunk,
  extractArrowStreams,
  hasCompletionSentinel
} from './report-arrow-ingestion'
import {
  type ColumnOptionsTable,
  listenColumnOptions
} from './report-column-options'
import {
  buildColumnMinWidths,
  buildMinWidthCssRules,
  headerFont,
  injectMinWidthSheet,
  reportColumnPositions
} from './report-column-widths'
import {
  type DatagridColumnsConfig,
  mergeReportDatagridConfig,
  preserveReportView,
  restoreReportDatagridConfig
} from './report-datagrid-config'
import {
  type LinkContextSplitPath,
  linkContextFromRow,
  linkFieldForReportCell,
  normalizeReportLinks,
  type ReportLinksConfig,
  type ReportRowClickPayload,
  renderReportLink,
  reportLinkColumns,
  reportRowClickPayload,
  reportRowClickPayloadSignature
} from './report-links'
import { createReportSession, type ReportSession } from './report-session'
import { resolveReportUiState } from './report-ui-state'

/*
 * Types
 */

type Schema = Record<string, string>

type ColumnConfig = DatagridColumnsConfig[string] &
  ExportColumnsConfig[string] & {
    field?: string
    icon?: string
    name?: string
  }

type ColumnsConfig = Record<string, ColumnConfig>

type TableColumns = Record<string, { name: string; icon: string }>

type TableConfig = {
  links: ReportLinksConfig
  id_columns: string[]
  columns: TableColumns
}

type Config = {
  table: TableConfig
  grid: ViewerConfigUpdate & {
    schema: Schema
    column_types: Schema
    columns_config?: ColumnsConfig
  }
}

type ColumnPayload = string | Readonly<{ field: string }>

type ColumnsPayload = {
  id: string
  columns?: readonly ColumnPayload[]
  sort?: []
  filter?: []
  group_by?: []
  split_by?: []
  input_id?: string
}

type DataPayload = {
  id: string
  data: Config
}

type RemovePayload = {
  id: string
  data: string[]
}

type ReportTable = ColumnOptionsTable & {
  update(buffer: ArrayBuffer): Promise<void>
  size(): Promise<number>
  delete(): Promise<void>
}

type ReportCellMeta = {
  readonly y?: number
  readonly value?: unknown
  readonly type?: unknown
  readonly size_key?: unknown
  readonly column_header?: readonly unknown[]
}

type ReportLinkTarget = {
  readonly columnHeader: LinkContextSplitPath
  readonly splitPath: LinkContextSplitPath
  readonly fullHeader: readonly string[]
  readonly rowHeaderDepth: number | undefined
  readonly rowHeaderField: string | undefined
  readonly linkabilityHeader: readonly string[]
  readonly linkField: string | undefined
}

const REPORT_ROW_LINK_ATTR = 'data-report-row-link'
const REPORT_ROW_LINK_PAYLOAD_SIGNATURE_ATTR = 'data-report-row-link-payload'
const REPORT_ROW_LINK_SELECTOR = `a[${REPORT_ROW_LINK_ATTR}="true"]`

/*
 * Hook
 */

const reportSessions = new WeakMap<HTMLElement, ReportSession>()

/** V5 leaves width and alignment sheets behind after their scoped table is deleted. */
const isOrphanedRegularTableSheet = (sheet: CSSStyleSheet): boolean => {
  try {
    const scopes = Array.from(sheet.cssRules).map(
      (rule) => /^\.rt-scope-\d+(?=\s)/.exec(rule.cssText)?.[0]
    )

    return (
      scopes.length > 0 &&
      scopes.every(
        (scope) => scope !== undefined && document.querySelector(scope) === null
      )
    )
  } catch {
    return false
  }
}

/** Remove only generated sheets whose owning table is no longer in the document. */
const dropLeakedRegularTableSheets = (): void => {
  document.adoptedStyleSheets = document.adoptedStyleSheets.filter(
    (sheet) => !isOrphanedRegularTableSheet(sheet)
  )
}

const reportCellFromTarget = (
  target: EventTarget | null
): HTMLTableCellElement | null =>
  target instanceof Element
    ? (target.closest('td, th') as HTMLTableCellElement | null)
    : null

const isTreeLabelCell = (cellEl: HTMLTableCellElement): boolean =>
  cellEl.classList.contains('psp-tree-label')

const isTreeAggregateRowCell = (cellEl: HTMLTableCellElement): boolean =>
  cellEl.closest('tr')?.querySelector('th.psp-tree-label') !== null

const isBodyCell = (cellEl: HTMLTableCellElement): boolean =>
  cellEl.tagName === 'TD' ||
  (cellEl.tagName === 'TH' && cellEl.closest('tbody') !== null)

const isLinkableReportCell = (
  cellEl: HTMLTableCellElement,
  header: readonly string[],
  linkField: string | undefined
): linkField is string =>
  header.length > 0 &&
  linkField !== undefined &&
  !isTreeLabelCell(cellEl) &&
  !isTreeAggregateRowCell(cellEl) &&
  isBodyCell(cellEl)

const reportAnchorFromTarget = (
  target: EventTarget | null
): HTMLAnchorElement | null =>
  target instanceof Element
    ? (target.closest(REPORT_ROW_LINK_SELECTOR) as HTMLAnchorElement | null)
    : null

const reportAnchorFromCell = (
  cellEl: HTMLTableCellElement
): HTMLAnchorElement | null =>
  cellEl.querySelector(REPORT_ROW_LINK_SELECTOR) as HTMLAnchorElement | null

const isPlainLeftMouseEvent = (event: MouseEvent): boolean =>
  event.button === 0 &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey

const isNewTabMouseEvent = (event: MouseEvent): boolean =>
  event.button === 1 || (event.button === 0 && (event.metaKey || event.ctrlKey))

export const FacadeReport = {
  mounted() {
    const element: HTMLElement = this.el
    const session = createReportSession((error) => {
      captureEffectError(error, {
        operation: 'report',
        payload: { reportId: element.id }
      })

      if (!session.signal.aborted) {
        element.querySelector('[data-part="loader"]')?.classList.add('hidden')
        element.querySelector('[data-part="error"]')?.classList.remove('hidden')
      }
    })
    reportSessions.set(element, session)
    session.onCleanup(
      listenColumnOptions((): ReportTable | undefined => this.table)
    )

    session.onCleanup(() => {
      // If torn down while an export is in progress, remove the beforeunload
      // guard and hide the overlay to prevent a stuck UI state.
      if (this.exporting) {
        window.removeEventListener('beforeunload', this.beforeUnloadGuard)
        this.exportProgressEl?.classList.add('hidden')
        this.exporting = false
      }

      this.unsubscribe?.()

      // Remove global stylesheets added by this hook instance so they don't
      // leak across LiveView navigations.
      if (this.minWidthSheet) {
        document.adoptedStyleSheets = document.adoptedStyleSheets.filter(
          (s) => s !== this.minWidthSheet
        )
      }
      dropLeakedRegularTableSheets()
    })

    // Custom-element registration can create a viewer before the first data batch.
    session.onCleanup(async () => {
      const viewer: HTMLPerspectiveViewerElement | undefined = this.viewerEl
      const table: ReportTable | undefined = this.table

      try {
        if (typeof viewer?.delete === 'function') {
          await viewer.delete()
        }
      } finally {
        await table?.delete()
      }
    })

    // Register handlers before loading so server updates wait for initialization.
    const handleReportEvent = <Payload>(
      name: string,
      handler: (payload: Payload) => void | Promise<void>
    ): void => {
      this.handleEvent(name, (payload: Payload) =>
        session.enqueue(() => handler(payload))
      )
    }

    /*
     * Helpers
     */

    const isTimestamp = (n: number) =>
      !Number.isNaN(n) && Number.isFinite(n) && /^\d+\.?\d+$/.test(n.toString())

    const normalizeSplitPath = (
      columnHeader: readonly unknown[] | undefined
    ): LinkContextSplitPath =>
      Array.isArray(columnHeader)
        ? columnHeader
            .filter(
              (segment): segment is string | number | boolean =>
                segment !== null && segment !== undefined
            )
            .map(String)
        : []

    const isReportCellMeta = (value: unknown): value is ReportCellMeta =>
      typeof value === 'object' && value !== null

    const rawReportField = (field: string): string =>
      field.startsWith(NG_PREFIX) ? field.slice(NG_PREFIX.length) : field

    const splitPathForHeaderField = (
      columnHeader: LinkContextSplitPath,
      field: string
    ): LinkContextSplitPath =>
      rawReportField(last(columnHeader) ?? '') === field
        ? columnHeader.slice(0, -1)
        : columnHeader

    const groupFieldForRowHeaderDepth = (
      groupBy: readonly string[],
      depth: number | undefined
    ): string | undefined =>
      depth === undefined || groupBy.length === 0
        ? undefined
        : groupBy[Math.min(depth, groupBy.length - 1)]

    const rowHeaderDepth = (meta: ReportCellMeta): number | undefined =>
      meta.type === 'row_header' &&
      typeof meta.size_key === 'number' &&
      Number.isInteger(meta.size_key) &&
      meta.size_key >= 0
        ? meta.size_key
        : undefined

    const reportLinkTargetForCell = (
      cellEl: HTMLTableCellElement,
      meta: ReportCellMeta,
      groupBy: readonly string[]
    ): ReportLinkTarget => {
      const columnHeader = normalizeSplitPath(meta.column_header)
      const fullHeader = [...groupBy, ...columnHeader]
      const currentRowHeaderDepth = rowHeaderDepth(meta)
      const rowHeaderField = groupFieldForRowHeaderDepth(
        groupBy,
        currentRowHeaderDepth
      )
      const bodyRowHeaderWithoutDepth =
        cellEl.tagName === 'TH' &&
        cellEl.closest('tbody') !== null &&
        currentRowHeaderDepth === undefined
      const linkField = bodyRowHeaderWithoutDepth
        ? undefined
        : linkFieldForReportCell({
            links: LINK_TEMPLATES,
            columnHeader,
            groupBy,
            rowHeaderDepth: currentRowHeaderDepth
          })
      const splitPath = linkField
        ? splitPathForHeaderField(columnHeader, linkField)
        : columnHeader
      const linkabilityHeader =
        currentRowHeaderDepth === undefined
          ? fullHeader
          : rowHeaderField
            ? [rowHeaderField]
            : []

      return {
        columnHeader,
        splitPath,
        fullHeader,
        rowHeaderDepth: currentRowHeaderDepth,
        rowHeaderField,
        linkabilityHeader,
        linkField
      }
    }

    const ENDPOINT = this.el.dataset.endpoint
    const SHOW_SETTINGS = Boolean(this.el.dataset.settings)
    const CONFIG = JSON.parse(this.el.dataset.config) as Config
    const EVENT_ON_SAVE = this.el.dataset.eventOnSave
    const EVENT_ON_ROW_CLICK = this.el.dataset.eventOnRowClick
    const TARGET = this.el.getAttribute('phx-target') || undefined
    const TITLE = this.el.dataset.title || undefined

    const ID_COLUMNS = CONFIG.table.id_columns

    const DEFAULT_CONFIG = {
      settings: SHOW_SETTINGS,
      plugin: 'Datagrid',
      plugin_config: {
        scroll_lock: false,
        edit_mode: 'READ_ONLY'
      }
    }

    const LINK_TEMPLATES = normalizeReportLinks(CONFIG.table.links)
    const LINK_PAYLOADS = new WeakMap<
      HTMLAnchorElement,
      ReportRowClickPayload
    >()
    let linkRevision = 0
    let hoveredReportCell: HTMLTableCellElement | undefined

    const pushReportRowClick = (payload: ReportRowClickPayload): void => {
      if (!EVENT_ON_ROW_CLICK) {
        return
      }

      pushEvent(
        this,
        EVENT_ON_ROW_CLICK,
        payload,
        TARGET,
        'Report row click failed'
      )
    }

    const clearReportRowLink = (cellEl: HTMLTableCellElement): void => {
      const anchor = reportAnchorFromCell(cellEl)

      if (!anchor) {
        return
      }

      const text = anchor.textContent ?? ''

      LINK_PAYLOADS.delete(anchor)
      cellEl.textContent = text
    }

    /** Invalidate pending lookups and links before cells can represent another row. */
    const invalidateReportLinks = (): void => {
      linkRevision += 1
      const cells = this.tableEl?.querySelectorAll('tbody td, tbody th') as
        | NodeListOf<HTMLTableCellElement>
        | undefined

      cells?.forEach(clearReportRowLink)
    }

    const openReportLinkInNewTab = (anchor: HTMLAnchorElement): void => {
      const href = anchor.getAttribute('href')

      if (!href) {
        return
      }

      window.open(href, '_blank', 'noopener')
    }

    const hasInstalledReportLinkAnchor = (
      cellEl: HTMLTableCellElement,
      href: string,
      payloadSignature: string
    ): boolean => {
      const anchor = reportAnchorFromCell(cellEl)

      return (
        anchor !== null &&
        LINK_PAYLOADS.get(anchor) !== undefined &&
        anchor.getAttribute('href') === href &&
        anchor.getAttribute(REPORT_ROW_LINK_PAYLOAD_SIGNATURE_ATTR) ===
          payloadSignature
      )
    }

    const reportAnchorClickHandler =
      (payload: ReportRowClickPayload) =>
      (clickEvent: MouseEvent): void => {
        if (!isPlainLeftMouseEvent(clickEvent)) {
          if (isNewTabMouseEvent(clickEvent)) {
            clickEvent.preventDefault()
            clickEvent.stopImmediatePropagation()
            const anchor = reportAnchorFromTarget(clickEvent.target)

            if (anchor && clickEvent.detail === 0) {
              openReportLinkInNewTab(anchor)
            }

            return
          }

          clickEvent.stopImmediatePropagation()
          return
        }

        if (!EVENT_ON_ROW_CLICK) {
          return
        }

        clickEvent.preventDefault()
        clickEvent.stopImmediatePropagation()

        pushReportRowClick(payload)
      }

    const installReportLinkAnchor = (
      cellEl: HTMLTableCellElement,
      href: string,
      payload: ReportRowClickPayload,
      payloadSignature: string
    ): void => {
      const anchor = document.createElement('a')

      anchor.setAttribute(REPORT_ROW_LINK_ATTR, 'true')
      anchor.setAttribute(
        REPORT_ROW_LINK_PAYLOAD_SIGNATURE_ATTR,
        payloadSignature
      )
      anchor.setAttribute('href', href)
      anchor.textContent = cellEl.textContent
      LINK_PAYLOADS.set(anchor, payload)
      anchor.addEventListener('click', reportAnchorClickHandler(payload), {
        capture: true,
        signal: session.signal
      })
      anchor.addEventListener('auxclick', reportAnchorClickHandler(payload), {
        capture: true,
        signal: session.signal
      })

      cellEl.replaceChildren(anchor)
    }

    const hoverReportCellFromEvent = (
      event: MouseEvent
    ): HTMLTableCellElement | undefined => {
      if (reportAnchorFromTarget(event.target)) {
        return undefined
      }

      const cellEl = reportCellFromTarget(event.target)

      if (!cellEl) {
        return undefined
      }

      const relatedTarget = event.relatedTarget

      return relatedTarget instanceof Node && cellEl.contains(relatedTarget)
        ? undefined
        : cellEl
    }

    const DB_CONFIG = pick(CONFIG.grid, [
      'columns',
      'filter',
      'group_by',
      'sort',
      'split_by',
      'columns_config',
      'aggregates',
      'expressions'
    ])

    // Group STRING columns by a null-coalescing expression so a null group key
    // renders as an expandable "Без групи" group rather than a non-expandable
    // leaf. The export strips these back to the raw columns (see export-client).
    const initialGroupBy = nullSafeGroupBy(
      DB_CONFIG.group_by ?? [],
      CONFIG.grid.column_types,
      DB_CONFIG.expressions ?? {}
    )

    const INITIAL_CONFIG = {
      ...DEFAULT_CONFIG,
      ...DB_CONFIG,
      group_by: initialGroupBy.group_by,
      expressions: initialGroupBy.expressions,
      columns: DB_CONFIG.columns
    }

    // The viewer omits grouped-only formatting; this adapter owns the full map.
    let reportColumnsConfig: DatagridColumnsConfig =
      CONFIG.grid.columns_config ?? {}

    /** Derive rendering-only visibility from the current report's link requirements. */
    const linkColumnConfig = async (
      config: Pick<ViewerConfigUpdate, 'columns' | 'group_by' | 'aggregates'>,
      columnsConfig: DatagridColumnsConfig
    ): Promise<
      Pick<ViewerConfigUpdate, 'columns' | 'aggregates'> & {
        readonly columns_config: DatagridColumnsConfig
      }
    > => {
      const { default: Mustache } = await session.wait(import('mustache'))
      const { columns, hiddenColumns } = reportLinkColumns(
        config.columns,
        config.group_by ?? [],
        ID_COLUMNS,
        LINK_TEMPLATES,
        Mustache
      )

      return {
        columns,
        aggregates: {
          ...omitBy(config.aggregates ?? {}, (_value, field) =>
            ID_COLUMNS.includes(field)
          ),
          ...Object.fromEntries(
            hiddenColumns.map((field) => [field, 'unique' as const])
          )
        },
        columns_config: {
          ...columnsConfig,
          ...Object.fromEntries(
            hiddenColumns.map((field) => [
              field,
              { ...columnsConfig[field], hidden: true }
            ])
          )
        }
      }
    }

    const restoreReportViewer = async (
      config: ViewerConfigUpdate,
      columnsConfig: DatagridColumnsConfig = reportColumnsConfig
    ): Promise<void> => {
      invalidateReportLinks()
      const viewer: HTMLPerspectiveViewerElement = this.viewerEl
      const current = await session.wait(viewer.save())
      const next = { ...current, ...config }
      const derived = await linkColumnConfig(next, columnsConfig)
      await session.wait(this.viewerEl.restore({ ...config, ...derived }))
      await session.wait(
        restoreReportDatagridConfig(
          element.querySelector<HTMLPerspectiveViewerDatagridPluginElement>(
            'perspective-viewer-datagrid'
          ),
          derived.columns_config,
          await session.wait(this.viewerEl.getView())
        )
      )
      await session.wait(this.viewerEl.flush())
      reportColumnsConfig = derived.columns_config
      await refreshColumnLayout()
    }

    this.loaderEl = this.el.querySelector('[data-part="loader"]')
    this.noDataEl = this.el.querySelector('[data-part="empty"]')
    this.errorEl = this.el.querySelector('[data-part="error"]')
    this.loaderCountEl = this.el.querySelector('[data-part="loader-count"]')
    this.loaderCountValueEl = this.el.querySelector(
      '[data-part="loader-count-value"]'
    )
    this.loaderStatusLoadingEl = this.el.querySelector(
      '[data-part="loader-status-loading"]'
    )
    this.loaderStatusRenderingEl = this.el.querySelector(
      '[data-part="loader-status-rendering"]'
    )

    this.exportProgressEl = this.el.querySelector(
      '[data-part="export-progress"]'
    )
    this.exportProgressLabelEl = this.el.querySelector(
      '[data-part="export-progress-label"]'
    )
    this.exportProgressBarEl = this.el.querySelector(
      '[data-part="export-progress-bar"]'
    )
    this.exportProgressCountEl = this.el.querySelector(
      '[data-part="export-progress-count"]'
    )

    this.exporting = false
    this.beforeUnloadGuard = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }

    this.viewerEl = element.querySelector(
      'perspective-viewer'
    ) as HTMLPerspectiveViewerElement

    const index = Object.keys(CONFIG.grid.schema).includes('id')
      ? 'id'
      : undefined

    /** Fit the completed report after applying its full column layout. */
    const resizeGrid = async (): Promise<void> => {
      await session.wait(this.viewerEl.resize())
    }

    // Column paths cover offscreen split buckets; scroll never changes these rules.
    let columnMinWidths: ReadonlyMap<string, number> | undefined

    /** Apply minimum widths to visible columns after a view shape changes. */
    const refreshColumnLayout = async (): Promise<void> => {
      const grid = element.querySelector<RegularTableElement>('regular-table')
      if (!grid) {
        return
      }

      const paths =
        element
          .querySelector<HTMLPerspectiveViewerDatagridPluginElement>(
            'perspective-viewer-datagrid'
          )
          ?.getVisibleColumnPaths() ?? []
      const headers = Array.from(
        grid.querySelectorAll<HTMLElement>('thead tr.rt-autosize th')
      )
      const meta = headers
        .map((cell) => grid.getMeta(cell))
        .find((value) => value && 'x' in value && typeof value.x === 'number')
      const reference = headers[0]
      if (!(meta && 'x' in meta) || typeof meta.x !== 'number' || !reference) {
        return
      }

      if (!columnMinWidths) {
        const context = document.createElement('canvas').getContext('2d')
        if (!context) {
          return
        }

        context.font = headerFont(reference)
        columnMinWidths = buildColumnMinWidths(
          Object.keys(CONFIG.table.columns),
          (field) => CONFIG.table.columns[field]?.name ?? field,
          context,
          ID_COLUMNS
        )
      }

      const positions = reportColumnPositions(paths, meta.size_key - meta.x)
      const scope = `#${CSS.escape(element.id)} regular-table table`
      const widthCss = buildMinWidthCssRules(positions, columnMinWidths, scope)
      if (widthCss === this.lastMinWidthCss) {
        return
      }

      this.lastMinWidthCss = widthCss
      this.minWidthSheet = injectMinWidthSheet(
        widthCss,
        this.minWidthSheet ?? null
      )
      // The new floor changes measured widths, but explicit user overrides stay intact.
      grid.resetAutoSize({
        auto: true,
        indices: true,
        override: false,
        row_height: false
      })
      await session.wait(grid.draw())
    }

    /** Localize body group labels without replacing their expand/collapse controls. */
    const setupGroupLabels = (): void => {
      const totalHeader = this.tableEl?.querySelector(
        'tbody > tr > th.psp-tree-label'
      )

      if (totalHeader && totalHeader.textContent === 'TOTAL') {
        totalHeader.textContent = 'Разом'
      }

      // The null group key is coalesced by the `__ng__*` expression to the ASCII
      // sentinel NO_GROUP_SENTINEL, so its tree-label cell now renders that
      // sentinel as ordinary group text (the row is a real expandable group, not
      // a null leaf). Relabel the sentinel to the human NO_GROUP_LABEL. We replace
      // ONLY the matching TEXT NODE, never the cell's textContent/innerText —
      // overwriting the whole cell wipes its sibling expand/collapse toggle.
      const rowHeaderCells = this.tableEl?.querySelectorAll('tbody th') as
        | NodeListOf<HTMLElement>
        | undefined

      for (const cell of rowHeaderCells ?? []) {
        const walker = document.createTreeWalker(cell, NodeFilter.SHOW_TEXT)
        let textNode = walker.nextNode()

        while (textNode) {
          if (textNode.nodeValue?.trim() === NO_GROUP_SENTINEL) {
            textNode.nodeValue = NO_GROUP_LABEL
          }

          textNode = walker.nextNode()
        }
      }
    }

    /** Label value columns while leaving tree headers empty for compact indentation. */
    const renderHeader: HeaderRenderer = ({ kind, field }) => {
      if (kind === 'group') {
        return undefined
      }

      const key = field.startsWith(NG_PREFIX)
        ? field.slice(NG_PREFIX.length)
        : field
      const column = CONFIG.table.columns[key]
      if (!column) {
        return undefined
      }

      const icon = createIcon(column.icon, 'regular', 'xs')

      return { label: column.name, icon }
    }

    /** Reconcile native viewer edits once without echoing an identical restore. */
    const reconcileLinkColumns = async (): Promise<void> => {
      const viewer: HTMLPerspectiveViewerElement = this.viewerEl
      const current = await session.wait(viewer.save())
      // save() returns the plugin's validated config through the viewer's generic JSON type.
      const currentColumnsConfig = (current.columns_config ??
        {}) as DatagridColumnsConfig
      const next = await linkColumnConfig(current, currentColumnsConfig)
      const changed = !(
        isDeepEqual(current.columns, next.columns) &&
        isDeepEqual(current.aggregates, next.aggregates) &&
        isDeepEqual(currentColumnsConfig, next.columns_config)
      )

      if (changed) {
        await restoreReportViewer(
          { columns: next.columns, aggregates: next.aggregates },
          mergeReportDatagridConfig(reportColumnsConfig, next.columns_config)
        )
      } else {
        reportColumnsConfig = mergeReportDatagridConfig(
          reportColumnsConfig,
          next.columns_config
        )
        await session.wait(
          restoreReportDatagridConfig(
            element.querySelector<HTMLPerspectiveViewerDatagridPluginElement>(
              'perspective-viewer-datagrid'
            ),
            reportColumnsConfig,
            await session.wait(viewer.getView())
          )
        )
        await refreshColumnLayout()
      }
    }

    // Initialise the viewer UI once the first Arrow batch has landed.
    // Deferred to avoid pivot/ExprTK re-render thrash on mid-stream load.
    const initViewerUI = async (
      Mustache: typeof import('mustache')
    ): Promise<void> => {
      // Drop width stylesheets leaked by regular-tables from previously-viewed
      // reports before this grid draws — it regenerates its own correct one.
      dropLeakedRegularTableSheets()

      const viewer: HTMLPerspectiveViewerElement = this.viewerEl
      await session.wait(preserveReportView(viewer))
      await session.wait(viewer.load(this.table))
      await session.wait(restoreReportViewer(INITIAL_CONFIG))

      this.tableEl = this.el.querySelector('regular-table')
      this.virtualPanelEl =
        this.tableEl?.shadowRoot.querySelector('.rt-virtual-panel')

      // Styles are now handled by static CSS rules

      const hostElement = element.querySelector('perspective-viewer')
      const shadowRoot = hostElement?.shadowRoot
      const toolbarStyle = document.createElement('style')

      toolbarStyle.textContent = `
        :host .sidebar_column,
        :host #modal_panel {
          background-image: none !important;
          background-color: var(--slate-2) !important;
        }

        :host label,
        :host #active-columns .column-selector-column:before,
        :host #column_settings_sidebar .column-selector-column:before,
        :host #sub-columns:before {
          font-weight: 600 !important;
          color: var(--slate-11) !important;
        }

        :host #settings_panel #plugin_selector_container .plugin-select-item-name {
          font-size: 0.85rem !important;
          color: var(--slate-11);
        }

        :host button#psp-expression-editor-button-save {
          color: transparent !important;
        }

        :host button#psp-expression-editor-button-save::before {
          content: "Створити";
          position: absolute;
          width: 100%;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--slate-11);
        }

        :host .column-selector-column .column-selector-column-border,
        :host .column-empty-input {
          background-color: var(--slate-4) !important;
        }

        :host .column-empty-input {
          color: var(--slate-12) !important;
        }

        :host .column_name {
          font-weight: 500;
          font-size: 11px;
        }

        /* Reports expose one grid through the application toolbar. */
        :host regular-layout-frame.single::part(titlebar),
        :host #settings_button {
          display: none !important;
        }
      `

      shadowRoot?.prepend(toolbarStyle)

      const datagrid =
        element.querySelector<HTMLPerspectiveViewerDatagridPluginElement>(
          'perspective-viewer-datagrid'
        )
      if (datagrid) {
        await session.wait(datagrid.setHeaderRenderer(renderHeader))
        session.onCleanup(() => datagrid.setHeaderRenderer(null))
      }
      setupGroupLabels()
      const grid = element.querySelector<RegularTableElement>('regular-table')

      this.unsubscribe = this.tableEl?.addStyleListener(() => {
        if (session.signal.aborted) {
          return
        }

        refreshReportLinks()
        setupGroupLabels()
      })

      /** Install a link only when its raw row satisfies the template's required fields. */
      const installReportRowLink = (
        cellEl: HTMLTableCellElement,
        target: ReportLinkTarget,
        rows: unknown
      ): void => {
        const field = target.linkField
        const row = linkContextFromRow(
          Array.isArray(rows) ? rows[0] : undefined,
          ID_COLUMNS,
          target.splitPath
        )
        const href = renderReportLink(
          field ? LINK_TEMPLATES[field] : undefined,
          row,
          ID_COLUMNS,
          window.location.search,
          Mustache.default
        )

        if (!(field && row && href)) {
          clearReportRowLink(cellEl)
          return
        }

        const payload = reportRowClickPayload(field, row)
        const signature = reportRowClickPayloadSignature(payload)

        if (!hasInstalledReportLinkAnchor(cellEl, href, signature)) {
          installReportLinkAnchor(cellEl, href, payload, signature)
        }
      }

      /** Reject async work after hover, redraw, configuration changes, or destruction. */
      const ownsReportCell = (
        cell: HTMLTableCellElement,
        revision: number
      ): boolean =>
        !session.signal.aborted && revision === linkRevision && cell.isConnected

      // Resolve links only while this report still owns the hovered cell.
      const updateReportCell = async (
        cellEl: HTMLTableCellElement
      ): Promise<void> => {
        const revision = ++linkRevision

        const rawMeta = this.tableEl.getMeta(cellEl) as unknown

        if (
          !isReportCellMeta(rawMeta) ||
          typeof rawMeta.y !== 'number' ||
          rawMeta.y < 0
        ) {
          return
        }

        const meta = rawMeta
        const view = await this.viewerEl.getView()
        session.signal.throwIfAborted()
        const config = await view.get_config()
        session.signal.throwIfAborted()
        if (!ownsReportCell(cellEl, revision)) {
          return
        }
        const groupBy = config.group_by ?? []
        const linkTarget = reportLinkTargetForCell(cellEl, meta, groupBy)
        const linkField = linkTarget.linkField

        if (
          !isLinkableReportCell(cellEl, linkTarget.linkabilityHeader, linkField)
        ) {
          clearReportRowLink(cellEl)
          return
        }

        const rows: unknown = await view.to_json({
          start_row: rawMeta.y,
          end_row: rawMeta.y + 1
        })
        session.signal.throwIfAborted()
        if (ownsReportCell(cellEl, revision)) {
          installReportRowLink(cellEl, linkTarget, rows)
        }
      }

      /** Refresh the cell under the pointer after drawing, including a recycled row. */
      const resolveHoveredReportLink = (): void => {
        if (hoveredReportCell?.isConnected) {
          updateReportCell(hoveredReportCell).catch((error: unknown) => {
            if (!session.signal.aborted) {
              captureEffectError(error, { operation: 'report:hover' })
            }
          })
        }
      }

      /** Clear old payloads before resolving the currently drawn row. */
      const refreshReportLinks = (): void => {
        invalidateReportLinks()
        resolveHoveredReportLink()
      }

      this.tableEl?.addEventListener(
        'mouseover',
        (event: MouseEvent) => {
          const cell = hoverReportCellFromEvent(event)

          if (cell) {
            hoveredReportCell = cell
            resolveHoveredReportLink()
          }
        },
        { signal: session.signal }
      )

      this.tableEl?.addEventListener('scroll', refreshReportLinks, {
        signal: session.signal
      })
      this.viewerEl.addEventListener(
        'perspective-config-update',
        () => {
          refreshReportLinks()
          session.enqueue(reconcileLinkColumns)
        },
        { signal: session.signal }
      )

      this.tableEl?.addEventListener(
        'contextmenu',
        (event: MouseEvent) => {
          if (reportAnchorFromTarget(event.target)) {
            // Preserve the browser menu by bypassing Perspective's panel handler.
            event.stopPropagation()
          }
        },
        { capture: true, signal: session.signal }
      )

      this.tableEl?.addEventListener(
        'mousedown',
        (event: MouseEvent) => {
          const anchor = reportAnchorFromTarget(event.target)

          if (!anchor) {
            return
          }

          if (isNewTabMouseEvent(event)) {
            event.preventDefault()
            openReportLinkInNewTab(anchor)
          }

          // Keep the grid's selection handler from consuming native anchor activation.
          event.stopImmediatePropagation()
        },
        { capture: true, signal: session.signal }
      )

      if (grid) {
        await session.wait(grid.draw())
      }
      await refreshColumnLayout()
    }

    /*
     * LV -> Report Events
     */

    handleReportEvent('report:restore', async (payload: DataPayload) => {
      if (payload.id === this.el.id) {
        await session.wait(this.viewerEl.flush())

        const config = pick(payload.data.grid, [
          'columns',
          'filter',
          'group_by',
          'sort',
          'split_by',
          'columns_config',
          'aggregates',
          'expressions'
        ])

        const safe = nullSafeGroupBy(
          config.group_by ?? [],
          CONFIG.grid.column_types,
          config.expressions ?? {}
        )

        await session.wait(
          restoreReportViewer(
            {
              ...config,
              group_by: safe.group_by,
              expressions: safe.expressions
            },
            config.columns_config ?? {}
          )
        )
      }
    })

    handleReportEvent('report:set-columns', async (payload: ColumnsPayload) => {
      if (payload.id === this.el.id) {
        if (payload.columns) {
          const columns = map(payload.columns, (column) =>
            typeof column === 'string' ? column : column.field
          )

          await session.wait(
            restoreReportViewer({
              columns
            })
          )
        }
      }
    })

    handleReportEvent('report:sort', async (payload: ColumnsPayload) => {
      if (payload.id === this.el.id) {
        if (payload.sort) {
          await session.wait(
            restoreReportViewer({
              sort: payload.sort
            })
          )
        }
      }
    })

    handleReportEvent('report:filter', async (payload: ColumnsPayload) => {
      if (payload.id === this.el.id) {
        if (payload.filter) {
          await session.wait(
            restoreReportViewer({
              filter: payload.filter
            })
          )

          if (payload.input_id) {
            document.getElementById(`filter-field-${payload.input_id}`)?.focus()
          }
        }
      }
    })

    handleReportEvent('report:group_by', async (payload: ColumnsPayload) => {
      if (payload.id === this.el.id) {
        if (payload.group_by) {
          const safe = nullSafeGroupBy(
            payload.group_by,
            CONFIG.grid.column_types,
            DB_CONFIG.expressions ?? {}
          )

          await session.wait(
            restoreReportViewer({
              group_by: safe.group_by,
              expressions: safe.expressions
            })
          )
        }
      }
    })

    handleReportEvent('report:split_by', async (payload: ColumnsPayload) => {
      if (payload.id === this.el.id) {
        if (payload.split_by) {
          await session.wait(
            restoreReportViewer({
              split_by: payload.split_by
            })
          )
        }
      }
    })

    handleReportEvent('report:save', async (payload: ColumnsPayload) => {
      if (payload.id === this.el.id && EVENT_ON_SAVE) {
        const data = await session.wait(this.viewerEl.save())

        pushEvent(
          this,
          EVENT_ON_SAVE,
          { id: this.el.id, data },
          TARGET,
          'Report save failed'
        )
        return
      }
    })

    handleReportEvent(
      'report:toggle-settings',
      async (payload: ColumnsPayload) => {
        if (payload.id === this.el.id) {
          await session.wait(this.viewerEl.toggleConfig())
        }
      }
    )

    // Shared descriptor input for BOTH the xlsx and csv exports \u2014 they MUST
    // route through the same schema/columnsConfig/idColumns/labels so the two
    // outputs stay structurally identical (translated headers, hidden id
    // columns, totals + null-group labels).
    const buildDescriptorInput = (): ExportDescriptorInput => {
      const gridColumnsConfig = CONFIG.grid.columns_config ?? {}
      const mergedColumnsConfig: ColumnsConfig = Object.fromEntries(
        Object.entries(CONFIG.table.columns).map(([field, tableCol]) => [
          field,
          { ...tableCol, ...(gridColumnsConfig[field] ?? {}) }
        ])
      )

      return {
        schema: CONFIG.grid.column_types,
        columnsConfig: mergedColumnsConfig,
        idColumns: ID_COLUMNS,
        noGroupLabel: NO_GROUP_LABEL,
        totalsLabel: this.exportProgressEl?.dataset.labelTotals ?? 'Totals'
      }
    }

    handleReportEvent('report:csv', async (payload: ColumnsPayload) => {
      if (payload.id === this.el.id) {
        this.exporting = true
        window.addEventListener('beforeunload', this.beforeUnloadGuard, {
          signal: session.signal
        })

        if (this.exportProgressEl) {
          this.exportProgressEl.classList.remove('hidden')
        }

        if (this.exportProgressLabelEl) {
          this.exportProgressLabelEl.textContent =
            this.exportProgressEl?.dataset.labelGeneratingCsv ??
            'Generating export\u2026'
        }

        // Indeterminate for CSV \u2014 hide bar and count
        if (this.exportProgressBarEl) {
          this.exportProgressBarEl.classList.add('hidden')
        }

        if (this.exportProgressCountEl) {
          this.exportProgressCountEl.textContent = ''
        }

        try {
          const csv = await session.wait(
            exportToCsv(this.viewerEl, buildDescriptorInput())
          )

          const universalBOM = '\uFEFF'
          const blob = new Blob([universalBOM + csv], {
            type: 'text/csv; charset=utf-8'
          })

          const csvLink = document.createElement('a')

          csvLink.href = URL.createObjectURL(blob)
          csvLink.target = '_blank'
          csvLink.download = `${TITLE ?? 'report'}.csv`
          csvLink.click()
          URL.revokeObjectURL(csvLink.href)
          csvLink.remove()
        } catch (err) {
          session.signal.throwIfAborted()
          console.error('[report] CSV export failed:', err)
          this.errorEl?.classList.remove('hidden')
        } finally {
          this.exporting = false
          window.removeEventListener('beforeunload', this.beforeUnloadGuard)

          if (!session.signal.aborted && this.exportProgressEl) {
            this.exportProgressEl.classList.add('hidden')
          }

          if (!session.signal.aborted && this.exportProgressBarEl) {
            this.exportProgressBarEl.classList.remove('hidden')
          }
        }
      }
    })

    handleReportEvent('report:xlsx', async (payload: ColumnsPayload) => {
      if (payload.id === this.el.id) {
        this.exporting = true
        window.addEventListener('beforeunload', this.beforeUnloadGuard, {
          signal: session.signal
        })

        if (this.exportProgressEl) {
          this.exportProgressEl.classList.remove('hidden')
        }

        if (this.exportProgressLabelEl) {
          this.exportProgressLabelEl.textContent =
            this.exportProgressEl?.dataset.labelPreparing ?? 'Preparing data…'
        }

        // Start indeterminate — bar at 0%, count empty
        const barFill = this.exportProgressBarEl?.querySelector('div') ?? null

        if (barFill) {
          barFill.style.width = '0%'
        }

        if (this.exportProgressBarEl) {
          this.exportProgressBarEl.setAttribute('aria-valuenow', '0')
        }

        if (this.exportProgressCountEl) {
          this.exportProgressCountEl.textContent = ''
        }

        try {
          const blob = await session.wait(
            exportToBlob(
              this.viewerEl,
              buildDescriptorInput(),
              (processed, total) => {
                if (session.signal.aborted) {
                  return
                }

                if (this.exportProgressLabelEl) {
                  this.exportProgressLabelEl.textContent =
                    this.exportProgressEl?.dataset.labelGeneratingXlsx ??
                    'Generating spreadsheet…'
                }

                const pct =
                  total > 0 ? Math.round((processed / total) * 100) : 0

                if (barFill) {
                  barFill.style.width = `${pct}%`
                }

                if (this.exportProgressBarEl) {
                  this.exportProgressBarEl.setAttribute(
                    'aria-valuenow',
                    String(pct)
                  )
                }

                if (this.exportProgressCountEl) {
                  this.exportProgressCountEl.textContent = `${processed.toLocaleString('uk-UA')} / ${total.toLocaleString('uk-UA')}`
                }
              }
            )
          )

          const link = document.createElement('a')

          link.href = URL.createObjectURL(blob)
          link.download = `${TITLE ?? 'report'}.xlsx`
          link.click()
          URL.revokeObjectURL(link.href)
          link.remove()
        } catch (err) {
          session.signal.throwIfAborted()
          console.error('[report] XLSX export failed:', err)
          this.errorEl?.classList.remove('hidden')
        } finally {
          this.exporting = false
          window.removeEventListener('beforeunload', this.beforeUnloadGuard)

          if (!session.signal.aborted && this.exportProgressEl) {
            this.exportProgressEl.classList.add('hidden')
          }
        }
      }
    })

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: PDF print handler requires complex data transformation
    handleReportEvent('report:print', async (payload: ColumnsPayload) => {
      if (payload.id === this.el.id) {
        const [
          { PdfMakeWrapper, Table, Txt, Cell },
          dayjs,
          localizedFormat,
          { loadInterFonts }
        ] = await session.wait(
          Promise.all([
            import('pdfmake-wrapper'),
            import('dayjs'),
            import('dayjs/plugin/localizedFormat'),
            import('../../fonts/inter-vfs')
          ])
        )

        /*
         * Init
         */

        dayjs.default.extend(localizedFormat.default)

        // Load Inter and JetBrains Mono fonts dynamically with base64 encoding for pdfmake
        const interFonts = await session.wait(loadInterFonts())

        PdfMakeWrapper.setFonts(interFonts, {
          inter: {
            normal: 'Inter-Regular.ttf',
            bold: 'Inter-Bold.ttf',
            italics: 'Inter-Regular.ttf',
            bolditalics: 'Inter-Bold.ttf'
          },
          jetbrainsMono: {
            normal: 'JetBrainsMono-Regular.ttf',
            bold: 'JetBrainsMono-Bold.ttf',
            italics: 'JetBrainsMono-Regular.ttf',
            bolditalics: 'JetBrainsMono-Bold.ttf'
          }
        })

        // biome-ignore lint/correctness/useHookAtTopLevel: PdfMakeWrapper method, not a React hook
        PdfMakeWrapper.useFont('inter')

        const view = await this.viewerEl.getView()
        session.signal.throwIfAborted()

        // Transposes report columns into PDF table rows.
        const zipColumns = (
          ...columns: readonly (readonly unknown[])[]
        ): unknown[][] =>
          Array.from(
            {
              length: Math.max(0, ...map(columns, (column) => column.length))
            },
            (_, index) => map(columns, (column) => column[index])
          )

        const columns = await view.to_columns()
        session.signal.throwIfAborted()
        const filteredColumns = omitBy(
          columns,
          (_val, key) => !isEmpty(intersection(key.split('|'), ID_COLUMNS))
        )

        // Calculate estimated table width based on actual content
        const groupDepth = columns.__ROW_PATH__?.[0]?.length || 0

        // Estimate character width for each column (in points at font size 6)
        const estimateColumnWidth = (
          columnData: unknown[],
          columnName: string
        ): number => {
          // Base width for column header
          const headerText = columnName?.split('|').join('\n') || ''
          const headerWidth = Math.max(headerText.length * 3.5, 20) // Minimum 20 points

          // Find max content width in the column
          let maxContentWidth = headerWidth

          if (Array.isArray(columnData)) {
            for (const cell of columnData) {
              if (cell !== null && cell !== undefined) {
                const cellStr = String(cell)
                // Estimate width: ~3.5 points per character at font size 6
                // Numbers are slightly wider in monospace font
                const isNum = isNumber(cell)
                const charWidth = isNum ? 4 : 3.5
                const cellWidth = cellStr.length * charWidth
                maxContentWidth = Math.max(maxContentWidth, cellWidth || 0)
              }
            }
          }

          // Add padding (10 points per column), ensure valid number
          return Math.max(maxContentWidth + 10, 30) // Minimum 30 points per column
        }

        // Calculate total estimated width with safety checks
        let totalTableWidth = 0

        // Add group columns width (if any)
        const safeGroupDepth = Math.max(0, groupDepth || 0)
        if (safeGroupDepth > 0) {
          // Estimate ~60 points per group level
          totalTableWidth += safeGroupDepth * 60
        }

        // Add data columns width
        for (const [colName, colData] of Object.entries(filteredColumns)) {
          if (colName !== '__ROW_PATH__') {
            const width = estimateColumnWidth(colData as unknown[], colName)
            totalTableWidth += Number.isNaN(width) ? 30 : width // Default to 30 if NaN
          }
        }

        // Ensure totalTableWidth is valid
        totalTableWidth = Math.max(100, totalTableWidth || 100) // Minimum 100 points

        // A4 page dimensions in points (72 points = 1 inch)
        // A4: 210mm × 297mm = 595 × 842 points
        // Accounting for margins (~40 points each side)
        const availableWidthPortrait = 595 - 80 // ~515 points
        const availableWidthLandscape = 842 - 80 // ~762 points

        // Determine orientation based on actual content width
        let pageOrientation: 'portrait' | 'landscape' = 'portrait'

        if (totalTableWidth > availableWidthPortrait) {
          pageOrientation = 'landscape'

          // If it still doesn't fit in landscape, we'll let pdfmake handle scaling
          if (totalTableWidth > availableWidthLandscape) {
          }
        }

        const pdf = new PdfMakeWrapper()

        pdf.pageSize('A4')
        pdf.pageOrientation(pageOrientation)

        const group = columns.__ROW_PATH__
        const head = Object.keys(filteredColumns)
          .filter((column) => column !== '__ROW_PATH__')
          .map((column) => {
            const text = column
              .split('|')
              .map((name) => CONFIG.table.columns[name]?.name || name)
              .join('\n')

            // Check if this is a numeric column
            const columnType = CONFIG.grid.schema[column.split('|')[0]]
            const isNumericColumn =
              columnType &&
              (columnType === 'integer' ||
                columnType === 'float' ||
                columnType === 'number' ||
                columnType === 'decimal')

            // Apply right alignment to numeric column headers
            if (isNumericColumn) {
              return new Txt(text).bold().alignment('right').end
            }
            return new Txt(text).bold().end
          })

        const groupMaxLength =
          group?.reduce((max: number, innerArray: unknown[]) => {
            const length = Array.isArray(innerArray) ? innerArray.length : 0
            return Math.max(max, length)
          }, 0) || 0

        const padSubarraysWithNulls = (
          targetLength: number,
          array: unknown[]
        ): unknown[] => {
          const currentLength = array.length
          const emptyToAdd = targetLength - currentLength

          if (array.length === 0) {
            // This is the "Total" label - keep in regular Inter font
            return [
              new Cell(new Txt('Разом').bold().end).colSpan(emptyToAdd).end,
              ...Array(emptyToAdd - 1).fill(null)
            ]
          }

          const cell = last(array)

          let cellValue = cell
          let isNumericValue = false

          if (
            isNumber(cell) &&
            cell.toString().length === 13 &&
            isTimestamp(cell)
          ) {
            cellValue = dayjs.default(cell).format('L')
          } else if (isNumber(cell)) {
            cellValue = Number(cell).toFixed(2)
            isNumericValue = true
          }

          // Check if this is a text label or numeric value
          const cellText = String(cellValue)
          const isTextLabel = /^[a-zA-Zа-яА-ЯіІїЇєЄґҐ]/.test(cellText)

          if (emptyToAdd > 0) {
            // This is a group total/subtotal row
            if (isNumericValue && !isTextLabel) {
              // Numeric total - use JetBrains Mono with right alignment
              return [
                new Cell(
                  new Txt(String(cellValue))
                    .bold()
                    .font('jetbrainsMono')
                    .alignment('right').end
                ).colSpan(targetLength).end,
                ...Array(emptyToAdd + array.length - 1).fill(null)
              ]
            }
            // Text label - use regular Inter
            return [
              new Cell(new Txt(String(cellValue)).bold().end).colSpan(
                targetLength
              ).end,
              ...Array(emptyToAdd + array.length - 1).fill(null)
            ]
          }

          // Regular cell
          if (isNumericValue && !isTextLabel) {
            return [
              new Cell(
                new Txt(String(cellValue))
                  .font('jetbrainsMono')
                  .alignment('right').end
              ).colSpan(targetLength).end,
              ...Array(emptyToAdd + array.length - 1).fill(null)
            ]
          }
          return [
            new Cell(new Txt(String(cellValue)).end).colSpan(targetLength).end,
            ...Array(emptyToAdd + array.length - 1).fill(null)
          ]
        }

        const body = group
          ? Object.values(filteredColumns).slice(1)
          : Object.values(filteredColumns)

        // Calculate column widths for better table layout
        const calculateColumnWidths = () => {
          const widths: (number | string)[] = []

          // Ensure groupMaxLength is a valid number
          const safeGroupMax = groupMaxLength || 0

          // Group columns get auto width
          for (let i = 0; i < safeGroupMax; i++) {
            widths.push('auto')
          }

          // Data columns get proportional width based on content
          // If table is too wide, use '*' for equal distribution
          const dataColumnsCount = Object.keys(filteredColumns).filter(
            (c) => c !== '__ROW_PATH__'
          ).length

          if (totalTableWidth > availableWidthLandscape) {
            // Table is very wide, use equal distribution
            for (let i = 0; i < dataColumnsCount; i++) {
              widths.push('*')
            }
          } else {
            // Table fits, use auto widths
            for (let i = 0; i < dataColumnsCount; i++) {
              widths.push('auto')
            }
          }

          return widths
        }

        // Calculate column widths before creating the table
        const columnWidths = calculateColumnWidths()

        const table = new Table([
          [
            ...(group
              ? [new Cell(new Txt('').end).colSpan(groupMaxLength).end]
              : []),
            ...Array.from({ length: groupMaxLength - 1 }, () => null),
            ...head.map((text) => text)
          ],
          ...zipColumns(...body).map((row, index) => [
            ...(group
              ? padSubarraysWithNulls(groupMaxLength, group[index])
              : []),
            // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Cell formatting requires handling multiple data types
            ...row.map((cell: unknown, cellIndex: number) => {
              let cellValue = cell
              let isNumeric = false

              if (
                isNumber(cell) &&
                cell.toString().length === 13 &&
                isTimestamp(cell)
              ) {
                cellValue = dayjs.default(cell).format('L')
              } else if (isNumber(cell)) {
                cellValue = Number(cell).toFixed(2)
                isNumeric = true
              }

              // Check if column is numeric type
              const columnKey = Object.keys(filteredColumns)[cellIndex]
              const columnType = CONFIG.grid.schema[columnKey?.split('|')[0]]
              const isNumericColumn =
                columnType &&
                (columnType === 'integer' ||
                  columnType === 'float' ||
                  columnType === 'number' ||
                  columnType === 'decimal')

              // Check if this is a group total row
              const isGroupTotal = group?.[index]?.length !== groupMaxLength

              // Check if the cell content looks numeric (not a text label)
              const cellText = String(cellValue)
              const isTextLabel = /^[a-zA-Zа-яА-ЯіІїЇєЄґҐ]/.test(cellText)
              const looksNumeric = /^[-−]?[\d\s,]+\.?\d*%?$/.test(
                cellText.replace(/[\s,]/g, '')
              )

              if (isGroupTotal) {
                // Group total row - check if value is numeric or text
                if (
                  (isNumeric || isNumericColumn || looksNumeric) &&
                  !isTextLabel
                ) {
                  // Numeric total - use JetBrains Mono bold with right alignment
                  return new Txt(String(cellValue))
                    .bold()
                    .font('jetbrainsMono')
                    .alignment('right').end
                }
                // Text label in total row - use regular Inter bold
                return new Txt(String(cellValue)).bold().end
              }
              if (
                (isNumeric || isNumericColumn || looksNumeric) &&
                !isTextLabel
              ) {
                // Regular numeric value - use JetBrains Mono with right alignment
                return new Txt(String(cellValue))
                  .font('jetbrainsMono')
                  .alignment('right').end
              }
              // Regular text value
              return String(cellValue)
            })
          ])
        ])
          .widths(columnWidths)
          .headerRows(1)
          .keepWithHeaderRows(1)
          .dontBreakRows(true)
          .fontSize(6).end

        const footer = (pagenumber: number, pagecount: number) => {
          return {
            margin: [40, 0],
            text: new Txt(`${pagenumber} / ${pagecount}`).fontSize(7).bold().end
          }
        }

        pdf.footer(footer)

        pdf.info({
          title: TITLE
        })

        pdf.add(
          new Txt([
            new Txt(TITLE).bold().end,
            new Txt(' / ').end,
            new Txt(new Date().toLocaleDateString()).end
          ])
            .fontSize(8)
            .margin([0, 0, 0, 10]).end
        )

        pdf.add(table)
        pdf.create().print()
      }
    })

    if (index) {
      handleReportEvent('report:update', async (payload: DataPayload) => {
        if (payload.id === this.el.id) {
          if (payload.data) {
            await this.table.update(payload.data)
            await session.wait(this.viewerEl.flush())
            await refreshColumnLayout()
          }
        }
      })

      handleReportEvent('report:remove', async (payload: RemovePayload) => {
        if (payload.id === this.el.id) {
          if (payload.data) {
            await this.table.remove(payload.data)
            await session.wait(this.viewerEl.flush())
            await refreshColumnLayout()
          }
        }
      })
    }
    session.start(async () => {
      const [{ loadReportEngine }, Mustache] = await session.wait(
        Promise.all([import('./report-engine'), import('mustache')])
      )
      const worker = await session.wait(loadReportEngine())
      const table = (await worker.table(
        CONFIG.grid.schema as unknown as Record<string, unknown[]>,
        { index }
      )) as ReportTable

      this.table = table
      session.signal.throwIfAborted()

      /*
       * Progressive Arrow ingestion
       *
       * The server streams a concatenation of per-batch self-framed Arrow IPC
       * streams. HTTP chunk boundaries do not align with Arrow message boundaries,
       * so we buffer bytes and use extractArrowStreams to find complete messages.
       *
       * The viewer is loaded and configured only after the first batch lands,
       * deferring the pivot/ExprTK render until real data is available.
       */
      const response = await session.wait(
        fetch(ENDPOINT, {
          headers: { Accept: 'application/vnd.apache.arrow.stream' },
          signal: session.signal
        })
      )

      let buf = new Uint8Array(0)
      let viewInitialised = false
      let threw = false
      let completed = false

      // Apply complete Arrow batches in order and retain only the unfinished tail.
      const ingestChunk = async (chunk: Uint8Array): Promise<void> => {
        const candidate = appendChunk(buf, chunk)
        completed ||= hasCompletionSentinel(candidate)
        const { streams, remaining, malformed } = extractArrowStreams(candidate)
        buf = remaining

        if (malformed) {
          console.warn(
            '[report] Arrow IPC stream contains a malformed message marker; partial data may be missing.'
          )
        }

        for (const stream of streams) {
          await session.wait(table.update(stream.buffer as ArrayBuffer))

          if (!viewInitialised) {
            await session.wait(initViewerUI(Mustache))
            viewInitialised = true
          }
        }

        if (streams.length > 0) {
          const loaded = await session.wait(table.size())

          if (this.loaderCountValueEl) {
            this.loaderCountValueEl.textContent = loaded.toLocaleString('uk-UA')
          }

          this.loaderCountEl?.classList.remove('hidden')
        }
      }

      // Fetch cancellation interrupts pending reads when the user leaves the report.
      const readStream = async (
        body: ReadableStream<Uint8Array>
      ): Promise<void> => {
        const reader = body.getReader()

        try {
          let chunk = await session.wait(reader.read())

          while (!chunk.done) {
            await ingestChunk(chunk.value)
            chunk = await session.wait(reader.read())
          }
        } catch {
          session.signal.throwIfAborted()
          threw = true
        } finally {
          try {
            await reader.cancel()
          } finally {
            reader.releaseLock()
          }
        }
      }

      if (response.ok && response.body) {
        await readStream(response.body)
      }

      const uiState = resolveReportUiState({
        responseOk: response.ok,
        threw,
        hadBatches: viewInitialised,
        completed
      })

      switch (uiState) {
        case 'ready':
          this.loaderStatusLoadingEl?.classList.add('hidden')
          this.loaderStatusRenderingEl?.classList.remove('hidden')
          await refreshColumnLayout()
          await session.wait(resizeGrid())
          await session.wait(this.viewerEl.flush())
          this.noDataEl.classList.add('hidden')
          break
        case 'empty':
          await session.wait(initViewerUI(Mustache))
          this.noDataEl.classList.remove('hidden')
          break
        case 'error':
          this.errorEl?.classList.remove('hidden')
          break
      }

      this.loaderEl.classList.add('hidden')
    })
  },

  destroyed() {
    reportSessions.get(this.el)?.destroy()
    reportSessions.delete(this.el)
  }
}
